import { useCallback, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import useGameUidParam from '@/lostHooks/gameUid/useGameUid';
import { useStore } from '@/store/useStore';
import { areUserSessionTokensReadyForGame } from '@/utilities/helpers/areUserSessionTokensReadyForGame';
import {
  clearVitalsStorage,
  maybeStartGameClientDurationVital,
  startMountVitals,
} from '@/utilities/logger/datadog/vitals';
import {
  trackGameExit,
  trackGameLaunch,
} from '@/utilities/logger/gameLifecycleTracking';

export {
  __resetGameLifecycleTrackingForTests,
  GAME_LIFECYCLE_AMPLITUDE_EVENTS,
} from '@/utilities/logger/gameLifecycleTracking';

/**
 * Orchestrates game lifecycle tracking across Datadog, Amplitude, and
 * Snowplow. Datadog launch vitals START once a valid session is ready
 * (initial load and re-auth after logout); analytics launch / exit are
 * exposed for callers and pagehide.
 *
 * Mirrors legacy `common/hooks/useGameLifecycleTracking.ts`.
 */
export const useGameLifecycleTracking = () => {
  const gameUid = useGameUidParam();
  const sessionResetRevision = useStore((s) => s.sessionResetRevision);
  const userSessionContext = useStore((s) => s.userSessionContext);
  const { isAndroid, isXsell } = useStore((s) => s.runtime?.wrapperType) ?? {
    isAndroid: false,
    isXsell: false,
  };
  const sessionReadyForLaunch = areUserSessionTokensReadyForGame(
    userSessionContext,
    isAndroid || isXsell,
  );
  const {
    gameUid: gameId,
    gameName,
    provider: gameProvider,
    gamingSessionTrackingId,
    isShortLinkLaunch,
  } = useStore(
    useShallow((s) => ({
      gameUid: s.gameContext.gameUid,
      gameName: s.gameContext.gameName,
      provider: s.gameContext.provider,
      gamingSessionTrackingId: s.gameContext.gamingSessionTrackingId,
      isShortLinkLaunch: s.gameContext.isShortLinkLaunch,
    })),
  );
  const userId = useStore((s) => s.userSessionContext.userId);
  const region = useStore((s) => s.runtime.region);

  useEffect(() => {
    if (!sessionReadyForLaunch) {
      return;
    }

    startMountVitals();
    maybeStartGameClientDurationVital();
  }, [gameUid, sessionResetRevision, sessionReadyForLaunch]);

  useEffect(() => {
    const clearVitals = () => clearVitalsStorage();
    window.addEventListener('pagehide', clearVitals);
    return () => window.removeEventListener('pagehide', clearVitals);
  }, []);

  const analyticsContext = useCallback(
    () => ({
      gameId,
      gameName,
      gameProvider,
      gamingSessionTrackingId,
      isShortLinkLaunch,
      region: region ?? undefined,
      userId,
    }),
    [
      gameId,
      gameName,
      gameProvider,
      gamingSessionTrackingId,
      isShortLinkLaunch,
      region,
      userId,
    ],
  );

  const fireGameLaunch = useCallback(() => {
    trackGameLaunch(analyticsContext());
  }, [analyticsContext]);

  const fireGameExit = useCallback(() => {
    trackGameExit(analyticsContext());
  }, [analyticsContext]);

  useEffect(() => {
    window.addEventListener('pagehide', fireGameExit);
    return () => window.removeEventListener('pagehide', fireGameExit);
  }, [fireGameExit]);

  return { fireGameLaunch, fireGameExit };
};

export default useGameLifecycleTracking;
